// ============================================================
// Create Upsize — Mock data & types
// Source: design-bundle SBData.jsx + UpsizeWizard.jsx
// ============================================================

import moment from 'moment';
import type { RatsRate } from '../../services/campaignManager/types';
import { AssetGroup } from '../../services/campaignManager/types';

// --- Types ---

export interface Brand {
  id: string;
  name: string;
  category: string;
}

export interface Merchant {
  id: string;
  merchantId: string;
  name: string;
  country: string;
  countryName: string;
  initial: string;
  brands: Brand[];
  campaigns: Campaign[];
}

export interface Campaign {
  id: string;
  name: string;
  start: string;
  end: string;
}

export interface BaselineTier {
  id: string;
  category: string;
  baseline: number;
  unit: '$' | '%';
}

export interface TierRate extends BaselineTier {
  rate: number;
  isAdded?: boolean;
  _touched?: boolean;
}

export interface PackageAsset {
  id: string;
  name: string;
  group: AssetGroup;
}

export interface ExposurePackage {
  id: string;
  key?: string;
  country?: string;
  name: string;
  tier: string;
  minCommissionRate: number;
  color: string;
  summary?: string;
  assets: PackageAsset[];
  typeOptions?: string[];
}

export interface StandaloneOffer {
  id: string;
  title: string;
  description: string;
  status: string;
  startDate: Date | null;
  endDate: Date | null;
  brand: string;
}

export interface UpsizeOpp {
  id: string;
  package: string | null;
  /** Derived from the selected package's typeOptions at pick-time. Defaults to 'UPSIZE'. */
  opportunityType?: 'UPSIZE' | 'FLASH_SALE';
  startDate: Date | null;
  endDate: Date | null;
  startTime: string;
  endTime: string;
  tiers: TierRate[];
  linkedStandaloneOfferIds: string[];
  campaignId: string | null;
  currency: string;
  networkProof: Array<{ uid: string; name: string; url?: string }>;
  // True once the merchant has actively touched either date field. The
  // wizard prefills `startDate` to today+LEAD_TIME_DAYS as a friendly
  // default (ShopBack review buffer); without this flag the prefill alone
  // would trip the isDirty check and pop the cancel-confirm modal on a
  // pristine Create flow. Flipped true on any DatePicker onChange.
  _dateTouched: boolean;
  /** Full baseline from GET /v2/mission-control/moop/baseline — stored for use at submit time */
  _baseline?: RatsRate | null;
}

export interface WizardData {
  brandId: string;
  opportunities: UpsizeOpp[];
  merchantComment: string;
}

// --- Constants ---

// Region timezone offsets. Used alongside COUNTRY_TZ_ABBR which provides the
// locale-specific abbreviation (SGT / MYT / ICT / …) that the prototype
// shows next to the offset.
export const COUNTRY_TZ: Record<string, string> = {
  SG: 'UTC+8',
  MY: 'UTC+8',
  ID: 'UTC+7',
  TH: 'UTC+7',
  VN: 'UTC+7',
  PH: 'UTC+8',
  TW: 'UTC+8',
  AU: 'UTC+11',
  KR: 'UTC+9',
};

export const COUNTRY_TZ_ABBR: Record<string, string> = {
  SG: 'SGT',
  MY: 'MYT',
  ID: 'WIB',
  TH: 'ICT',
  VN: 'ICT',
  PH: 'PHT',
  TW: 'TWT',
  AU: 'AEDT',
  KR: 'KST',
};

// Source of truth: `.claude/handoff/ui/SBData.jsx` (`GLOBAL_CURRENCIES`).
// Synced 2026-05-30 — full 17-currency list (was 10, missing the European /
// Northeast-Asia / India set).
export const GLOBAL_CURRENCIES = [
  'SGD',
  'USD',
  'MYR',
  'IDR',
  'PHP',
  'THB',
  'VND',
  'TWD',
  'HKD',
  'KRW',
  'JPY',
  'CNY',
  'AUD',
  'NZD',
  'EUR',
  'GBP',
  'INR',
];

// --- Empty merchant fallback (replaces mock "Expedia" data) ---
// Used as a safe fallback when initInfo.merchant is null. Shows blanks instead of fake data.

export const MOCK_MERCHANT: Merchant = {
  id: '',
  merchantId: '',
  name: '',
  country: '',
  countryName: '',
  initial: '',
  brands: [],
  campaigns: [],
};

// --- Upsize taxonomy ---
// Source of truth: `.claude/handoff/ui/SBData.jsx` (`UPSIZE_TYPES`, `UPSIZE_SUBTYPES`,
// `UPSIZE_SUBTYPE_COLOR`). Synced 2026-05-30.

export interface UpsizeType {
  key: 'UPSIZE';
  label: string;
  description: string;
}

export interface UpsizeSubtype {
  key: 'PLATINUM' | 'GOLD' | 'SILVER';
  label: 'Platinum' | 'Gold' | 'Silver';
  description: string;
}

export const UPSIZE_TYPES: UpsizeType[] = [
  // BASELINE is BD-owned per PRD — not shown to merchants
  { key: 'UPSIZE', label: 'Upsize', description: 'Increase commission + Cashback for a fixed period.' },
];

export const UPSIZE_SUBTYPES: UpsizeSubtype[] = [
  { key: 'PLATINUM', label: 'Platinum', description: 'Top-tier rate boost — premium merchants.' },
  { key: 'GOLD', label: 'Gold', description: 'Mid-tier rate boost.' },
  { key: 'SILVER', label: 'Silver', description: 'Entry-tier rate boost.' },
];

// Used for status-tag coloring on detail pages + catalog tables.
// Mapped to antd `<Tag color>` values (purple/gold/blue) — accepts both
// the user-facing labels and the KEY-cased variants for robustness.
export const UPSIZE_SUBTYPE_COLOR: Record<string, string> = {
  Platinum: 'purple',
  Gold: 'gold',
  Silver: 'blue',
  PLATINUM: 'purple',
  GOLD: 'gold',
  SILVER: 'blue',
};

// --- Baseline tiers (Expedia SG) ---

export const BASELINE_TIERS: BaselineTier[] = [
  { id: 'flight', category: 'Flights', baseline: 1.28, unit: '$' },
  { id: 'car', category: 'Car Rental', baseline: 7.0, unit: '%' },
  { id: 'hotels', category: 'Hotels', baseline: 7.8, unit: '%' },
  { id: 'package', category: 'Hotel + Flight Package', baseline: 1.8, unit: '%' },
  { id: 'activity', category: 'Activities', baseline: 7.0, unit: '%' },
];

// --- Exposure packages (SG catalog) ---
// Source of truth: `.claude/handoff/ui/SBData.jsx` (`EXPOSURE_PACKAGES` constant).
// Synced 2026-05-30 to mirror the prototype 1:1 — 11 packages, full asset taxonomy.
// When the prototype updates, mirror here. Do not invent packages locally.

export const EXPOSURE_PACKAGES: ExposurePackage[] = [
  {
    id: 'shopfest-diamond',
    name: 'ShopFest Diamond',
    tier: 'Flagship',
    minCommissionRate: 14,
    color: '#6366F1',
    summary: 'Flagship event placement across app, web and outbound.',
    assets: [
      { id: '88826', name: 'ShopFest Home Screen Brand Spotlight', group: 'MANUAL' },
      { id: '88827', name: 'ShopFest 15" Native Video or GIF', group: 'MANUAL' },
      { id: '88830', name: 'Search Ads 1', group: 'MANUAL' },
      { id: '88832', name: 'Cheatsheet', group: 'MANUAL' },
      { id: '88833', name: 'Social Cheatsheet — Brand Mention', group: 'MANUAL' },
      { id: '88834', name: 'Reminder feature (Add to watchlist)', group: 'MANUAL' },
      { id: '100000001', name: 'Promotional Email — solus', group: 'CRM' },
      { id: '100000003', name: 'Promotional Email — tile', group: 'EDM' },
      { id: '100000006', name: 'Promotional Email — Grid', group: 'EDM' },
      { id: '110000004', name: 'FB/IG Story — brand mention', group: 'MANUAL' },
      { id: '150000001', name: 'Line Post — dedicated', group: 'MANUAL' },
      { id: '190000001', name: 'Web Category Screen Hero banner', group: 'AUTO' },
      { id: '010000001', name: 'App Search History Banner — All tab', group: 'AUTO' },
      { id: '020000001', name: 'App Search Autocomplete Banner — All tab', group: 'AUTO' },
      { id: '040000001', name: 'App Category Screen Hero banner', group: 'AUTO' },
      { id: '050000004', name: 'App Campaign Screen Marketing Asset 4', group: 'AUTO' },
      { id: '070000002', name: 'Web Homepage Spotlight 1', group: 'AUTO' },
      { id: '080000001', name: 'Pop up', group: 'CRM' },
      { id: '090000001', name: 'Push Notification — solus', group: 'CRM' },
    ],
  },
  {
    id: 'super-brand-day',
    name: 'Super Brand Day',
    tier: 'Premium',
    minCommissionRate: 14,
    color: '#6366F1',
    summary: 'Full-funnel brand-day takeover with heavy app coverage.',
    assets: [
      { id: '88812', name: 'Merchant UMP (Manual) — Brand Header image', group: 'MANUAL' },
      { id: '88814', name: 'Merchant UMP (Manual) — Hero Banner', group: 'MANUAL' },
      { id: '100000003', name: 'Promotional Email — tile', group: 'EDM' },
      { id: '100000006', name: 'Promotional Email — Grid', group: 'EDM' },
      { id: '160000001', name: 'Web Campaign Screen Marketing Asset 1', group: 'AUTO' },
      { id: '190000001', name: 'Web Category Screen Hero banner', group: 'AUTO' },
      { id: '000010001', name: 'App UHS Masthead', group: 'AUTO' },
      { id: '000010002', name: 'App UHS Upsized Cashback', group: 'AUTO' },
      { id: '000010003', name: 'App — UHS — OA — Spotlight 1', group: 'AUTO' },
      { id: '010000001', name: 'App Search History Banner — All tab', group: 'AUTO' },
      { id: '010000002', name: 'App Search History Deals — All tab', group: 'AUTO' },
      { id: '020000001', name: 'App Search Autocomplete Banner — All tab', group: 'AUTO' },
      { id: '030000001', name: 'App Sponsored Results (Keywords based)', group: 'AUTO' },
      { id: '040000001', name: 'App Category Screen Hero banner', group: 'AUTO' },
      { id: '070000001', name: 'Web Homepage Hero banner', group: 'AUTO' },
      { id: '070000003', name: 'Web Homepage Spotlight 2', group: 'AUTO' },
      { id: '080000001', name: 'Pop up', group: 'CRM' },
      { id: '090000001', name: 'Push Notification — solus', group: 'CRM' },
    ],
  },
  {
    id: 'campaign-platinum-plus',
    name: 'Campaign Platinum Plus',
    tier: 'Platinum+',
    minCommissionRate: 14,
    color: '#6366F1',
    summary: 'Premium campaign placement with email coverage.',
    assets: [
      { id: '88831', name: 'Flash Sale — New (Optional for BD to sell)', group: 'MANUAL' },
      { id: '100000003', name: 'Promotional Email — tile', group: 'EDM' },
      { id: '100000004', name: 'Promotional Email — Flash sales', group: 'EDM' },
      { id: '100000006', name: 'Promotional Email — Grid', group: 'EDM' },
      { id: '160000001', name: 'Web Campaign Screen Marketing Asset 1', group: 'AUTO' },
      { id: '190000001', name: 'Web Category Screen Hero banner', group: 'AUTO' },
      { id: '000010001', name: 'App UHS Masthead', group: 'AUTO' },
      { id: '000010002', name: 'App UHS Upsized Cashback', group: 'AUTO' },
      { id: '040000001', name: 'App Category Screen Hero banner', group: 'AUTO' },
      { id: '050000001', name: 'App Campaign Screen Marketing Asset 1', group: 'AUTO' },
      { id: '070000001', name: 'Web Homepage Hero banner', group: 'AUTO' },
      { id: '070000003', name: 'Web Homepage Spotlight 2', group: 'AUTO' },
    ],
  },
  {
    id: 'campaign-platinum',
    name: 'Campaign Platinum',
    tier: 'Platinum',
    minCommissionRate: 12,
    color: '#6366F1',
    summary: 'Campaign-grade placement, slimmer than Platinum Plus.',
    assets: [
      { id: '88831', name: 'Flash Sale — New (Optional for BD to sell)', group: 'MANUAL' },
      { id: '100000003', name: 'Promotional Email — tile', group: 'EDM' },
      { id: '100000006', name: 'Promotional Email — Grid', group: 'EDM' },
      { id: '160000001', name: 'Web Campaign Screen Marketing Asset 1', group: 'AUTO' },
      { id: '190000001', name: 'Web Category Screen Hero banner', group: 'AUTO' },
      { id: '000010002', name: 'App UHS Upsized Cashback', group: 'AUTO' },
      { id: '040000001', name: 'App Category Screen Hero banner', group: 'AUTO' },
      { id: '050000001', name: 'App Campaign Screen Marketing Asset 1', group: 'AUTO' },
      { id: '070000001', name: 'Web Homepage Hero banner', group: 'AUTO' },
      { id: '070000003', name: 'Web Homepage Spotlight 2', group: 'AUTO' },
    ],
  },
  {
    id: 'bau-platinum',
    name: 'BAU Platinum',
    tier: 'Platinum (always-on)',
    minCommissionRate: 10,
    color: '#6366F1',
    summary: 'Always-on premium placement for non-event periods.',
    assets: [
      { id: '100000003', name: 'Promotional Email — tile', group: 'EDM' },
      { id: '100000006', name: 'Promotional Email — Grid', group: 'EDM' },
      { id: '160000001', name: 'Web Campaign Screen Marketing Asset 1', group: 'AUTO' },
      { id: '190000001', name: 'Web Category Screen Hero banner', group: 'AUTO' },
      { id: '000010002', name: 'App UHS Upsized Cashback', group: 'AUTO' },
      { id: '040000001', name: 'App Category Screen Hero banner', group: 'AUTO' },
      { id: '050000001', name: 'App Campaign Screen Marketing Asset 1', group: 'AUTO' },
      { id: '070000001', name: 'Web Homepage Hero banner', group: 'AUTO' },
      { id: '070000003', name: 'Web Homepage Spotlight 2', group: 'AUTO' },
    ],
  },
  {
    id: 'gold',
    name: 'Gold',
    tier: 'Gold',
    minCommissionRate: 8,
    color: '#D4A017',
    summary: 'Mid-tier featured-stores placement with email.',
    assets: [
      { id: '100000003', name: 'Promotional Email — tile', group: 'EDM' },
      { id: '100000006', name: 'Promotional Email — Grid', group: 'EDM' },
      { id: '160000002', name: 'Web Campaign Screen Marketing Asset 2', group: 'AUTO' },
      { id: '190000002', name: 'Web Category Screen Featured stores', group: 'AUTO' },
      { id: '040000002', name: 'App Category Screen Featured stores', group: 'AUTO' },
      { id: '050000002', name: 'App Campaign Screen Marketing Asset 2', group: 'AUTO' },
    ],
  },
  {
    id: 'category-bundle',
    name: 'Category Bundle',
    tier: 'Category-focused',
    minCommissionRate: 0,
    color: '#9CA3AF',
    summary: 'Entry-level category-screen hero on app and web.',
    assets: [
      { id: '190000001', name: 'Web Category Screen Hero banner', group: 'AUTO' },
      { id: '040000001', name: 'App Category Screen Hero banner', group: 'AUTO' },
    ],
  },
  {
    id: 'epic-hourly-am',
    name: 'Epic Hourly 12am–2am',
    tier: 'Hourly slot (late night)',
    minCommissionRate: 8,
    color: '#9CA3AF',
    summary: 'Short-burst placement, 12am–2am window.',
    assets: [
      { id: '050000005', name: 'App Campaign Screen Marketing Asset 5', group: 'AUTO' },
      { id: '050000006', name: 'App Campaign Screen Marketing Asset 6', group: 'AUTO' },
    ],
  },
  {
    id: 'epic-hourly-pm',
    name: 'Epic Hourly 12pm–2pm',
    tier: 'Hourly slot (lunch)',
    minCommissionRate: 8,
    color: '#9CA3AF',
    summary: 'Short-burst placement, 12pm–2pm window.',
    assets: [
      { id: '050000007', name: 'App Campaign Screen Marketing Asset 7', group: 'AUTO' },
      { id: '050000008', name: 'App Campaign Screen Marketing Asset 8', group: 'AUTO' },
    ],
  },
  {
    id: 'epic-hourly-night',
    name: 'Epic Hourly 10pm–12MN',
    tier: 'Hourly slot (evening)',
    minCommissionRate: 8,
    color: '#9CA3AF',
    summary: 'Short-burst placement, 10pm–12 midnight window.',
    assets: [
      { id: '050000009', name: 'App Campaign Screen Marketing Asset 9', group: 'AUTO' },
      { id: '050000010', name: 'App Campaign Screen Marketing Asset 10', group: 'AUTO' },
    ],
  },
  {
    id: 'no-package',
    name: 'No Package',
    tier: 'Commission uplift only',
    minCommissionRate: 0,
    color: '#9CA3AF',
    summary: 'Commission uplift only — no exposure placements bundled.',
    assets: [],
  },
];

// --- Blocked dates (per package) ---
// Mock inventory data. Source of truth in production: real inventory API.
//
// Used by `availabilityFor()` below to paint the green/red dot on date pickers
// (see `moop-shared/AvailabilityDatePicker`). The dot is purely visual — booked
// dates remain SELECTABLE (per design 2026-05-30: "纯展示，用户仍可选"). The
// rationale is expectation-setting: merchants see what's likely tight, but
// ShopBack still reviews every submission and may approve / negotiate.

export const BLOCKED_DATES_BY_PACKAGE: Record<string, string[]> = {
  'category-bundle': ['2026-05-11', '2026-05-12', '2026-05-13', '2026-08-09'],
  gold: [
    '2026-05-09',
    '2026-05-10',
    '2026-05-11',
    '2026-05-12',
    '2026-05-13',
    '2026-06-12',
    '2026-06-13',
    '2026-08-09',
    '2026-08-10',
  ],
  'bau-platinum': [
    '2026-05-08',
    '2026-05-09',
    '2026-05-10',
    '2026-05-11',
    '2026-05-12',
    '2026-05-13',
    '2026-05-14',
    '2026-06-12',
    '2026-06-13',
    '2026-06-14',
    '2026-07-04',
    '2026-08-09',
    '2026-08-10',
    '2026-08-11',
    '2026-11-11',
  ],
};

/**
 * Return the availability state for a given package on a given day.
 * `null` package → no signal (no package selected yet).
 *
 * 2-state per design 2026-05-30: only `available` ↔ `sold-out`.
 * Sold-out is a visual hint; the user can still pick it. See `AvailabilityDatePicker`.
 */
export const availabilityFor = (
  packageId: string | null | undefined,
  date: Date | moment.Moment
): 'healthy' | 'sold-out' | null => {
  if (!packageId) return null;
  const list = BLOCKED_DATES_BY_PACKAGE[packageId];
  if (!list) return 'healthy';
  const ymd = moment.isMoment(date) ? date.format('YYYY-MM-DD') : moment(date).format('YYYY-MM-DD');
  return list.includes(ymd) ? 'sold-out' : 'healthy';
};

// --- Seed standalone offers ---

// Seed offers across the 8 mock brands so the FeaturedOfferSection picker
// has data regardless of which brand the merchant selects. Real offers
// come from the merchant's own catalogue at runtime; this list is dev-only.
export const SEED_OFFERS: StandaloneOffer[] = [
  {
    id: 'offer-1',
    title: 'Free shipping on orders S$50+',
    description: 'Use code SHIPFREE at checkout. T&Cs apply.',
    status: 'APPROVED',
    startDate: new Date('2026-05-20'),
    endDate: new Date('2026-06-30'),
    brand: 'Expedia',
  },
  {
    id: 'offer-2',
    title: '10% off all hotels',
    description: 'Exclusive ShopBack rate — auto-applied at checkout.',
    status: 'PENDING_SB_REVIEW',
    startDate: new Date('2026-06-01'),
    endDate: new Date('2026-06-15'),
    brand: 'Expedia',
  },
  {
    id: 'offer-3',
    title: 'Summer travel flash deal',
    description: 'Up to 20% off selected flights + hotels.',
    status: 'DRAFT',
    startDate: new Date('2026-07-01'),
    endDate: new Date('2026-07-31'),
    brand: 'Expedia',
  },
  {
    id: 'offer-4',
    title: 'S$30 off APAC flights',
    description: 'Min spend S$500. Selected routes only.',
    status: 'APPROVED',
    startDate: new Date('2026-05-15'),
    endDate: new Date('2026-08-31'),
    brand: 'Expedia Asia',
  },
  {
    id: 'offer-5',
    title: 'Free breakfast at partner hotels',
    description: 'Auto-included on selected hotel bookings.',
    status: 'APPROVED',
    startDate: new Date('2026-06-01'),
    endDate: new Date('2026-09-30'),
    brand: 'Expedia Stays',
  },
  {
    id: 'offer-6',
    title: 'Stay 3, pay 2',
    description: 'Third night free on participating properties.',
    status: 'PENDING_SB_REVIEW',
    startDate: new Date('2026-07-01'),
    endDate: new Date('2026-08-31'),
    brand: 'Expedia Stays',
  },
  {
    id: 'offer-7',
    title: '15% off long-haul flights',
    description: 'Bookings to Europe & US — auto-applied.',
    status: 'APPROVED',
    startDate: new Date('2026-05-01'),
    endDate: new Date('2026-10-31'),
    brand: 'Expedia Flights',
  },
  {
    id: 'offer-8',
    title: 'S$50 off car rentals',
    description: 'Min 3-day rental. Pick-up at SG / KL airports.',
    status: 'DRAFT',
    startDate: new Date('2026-06-15'),
    endDate: new Date('2026-12-31'),
    brand: 'Expedia Cars',
  },
  {
    id: 'offer-9',
    title: 'Free cabin upgrade',
    description: 'Eligible on 7+ night cruises booked by Aug.',
    status: 'APPROVED',
    startDate: new Date('2026-05-01'),
    endDate: new Date('2026-08-31'),
    brand: 'Expedia Cruises',
  },
  {
    id: 'offer-10',
    title: 'Bundle & save 25%',
    description: 'Flight + Hotel package savings.',
    status: 'APPROVED',
    startDate: new Date('2026-06-01'),
    endDate: new Date('2026-11-30'),
    brand: 'Expedia Packages',
  },
  {
    id: 'offer-11',
    title: 'Corporate rates unlocked',
    description: 'Up to 18% off for business travellers.',
    status: 'PENDING_SB_REVIEW',
    startDate: new Date('2026-05-01'),
    endDate: new Date('2026-12-31'),
    brand: 'Expedia for Business',
  },
];

// --- Helpers ---

export const makeBlankTiers = (baselineTiers: BaselineTier[] = BASELINE_TIERS): TierRate[] =>
  baselineTiers.map((t) => ({ ...t, rate: t.baseline, _touched: false }));

// Calendar-day buffer ShopBack typically needs to review + set up a campaign.
// Used to prefill the start date as a friendly default and to drive the
// soft buffer hint shown when the merchant picks an earlier date.
export const LEAD_TIME_DAYS = 5;

export const todayPlus = (days: number): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d;
};

export const makeBlankOpp = (index: number): UpsizeOpp => ({
  id: `opp-${Date.now()}-${index}`,
  package: null,
  startDate: todayPlus(LEAD_TIME_DAYS),
  endDate: null,
  startTime: '00:00',
  endTime: '23:59',
  tiers: makeBlankTiers([]),
  linkedStandaloneOfferIds: [],
  campaignId: null,
  currency: 'SGD',
  networkProof: [],
  _dateTouched: false,
});

export const makeInitialData = (): WizardData => ({
  brandId: '',
  opportunities: [makeBlankOpp(1)],
  merchantComment: '',
});

export const REQUIRED_FIELD_COUNT = 4; // package, startDate, endDate, at least 1 tier rate > baseline

export const countFilledFields = (opp: UpsizeOpp): number => {
  let count = 0;
  if (opp.package) count += 1;
  if (opp.startDate) count += 1;
  if (opp.endDate) count += 1;
  const hasValidRate = opp.tiers.some((t) => t.rate > 0 && t.rate >= t.baseline);
  if (hasValidRate) count += 1;
  return count;
};

export const isOppComplete = (opp: UpsizeOpp): boolean => countFilledFields(opp) === REQUIRED_FIELD_COUNT;

// Combine a date + HH:mm string into a single moment — same combination the
// submit mapper uses (wizardMappers.toIsoDatetime), so the ordering we validate
// matches the ordering we POST.
export const combineDateAndTime = (date: Date, time: string): moment.Moment => {
  const [h, min] = (time || '').split(':').map(Number);
  return moment(date)
    .hour(h || 0)
    .minute(min || 0)
    .second(0)
    .millisecond(0);
};

/** Core start→end check at full datetime granularity (date + time). Equal is allowed. */
export const isEndBeforeStart = (startDate: Date, startTime: string, endDate: Date, endTime: string): boolean =>
  combineDateAndTime(endDate, endTime).isBefore(combineDateAndTime(startDate, startTime));

/** True when both dates are set and the end datetime is before the start datetime.
 *  The end picker's disabledDate only guards calendar days, so a same-day end time
 *  earlier than the start time (or a stale end left behind when the start moves later)
 *  slips through without this check. Presence is handled separately by isOppComplete. */
export const isOppDateRangeInvalid = (opp: UpsizeOpp): boolean => {
  if (!opp.startDate || !opp.endDate) return false;
  return isEndBeforeStart(opp.startDate, opp.startTime, opp.endDate, opp.endTime);
};

/** True when the start datetime (date + time) is before the current moment.
 *  The calendar disabledDate guard only operates at day granularity — a start
 *  time of 00:00 on today is still in the past once any part of the day has
 *  elapsed. Mirrors Needle's "Start date should be in the future" check. */
export const isStartInPast = (opp: UpsizeOpp): boolean => {
  if (!opp.startDate) return false;
  return combineDateAndTime(opp.startDate, opp.startTime).isBefore(moment());
};

// Any touched, baseline-violating tier blocks Continue. Mirrors prototype
// UpsizeWizard.jsx:198 — gates submission so the merchant fixes invalid rates
// before reaching Review (rather than discovering it at submit time).
export const tiersHaveBaselineViolation = (tiers: UpsizeOpp['tiers']): boolean =>
  tiers.some((t) => !t.isAdded && t._touched && t.baseline !== null && Number(t.rate) < Number(t.baseline));

/** Resolve the ExposurePackage for a given ref (key+country preferred, id fallback). */
export const resolveExposurePackage = (
  packages: ExposurePackage[],
  ref: string | null,
  country?: string
): ExposurePackage | undefined => {
  if (!ref) return undefined;
  return packages.find((p) =>
    p.key && ref ? p.key === ref && (!country || !p.country || p.country === country) : p.id === ref
  );
};

/** True when any %-tier commission rate is below the package's minCommissionRate floor.
 *  No _touched guard — it's an absolute floor; auto-fill pre-satisfies it on selection,
 *  so this only bites when the merchant manually edits a %-tier back below the minimum. */
export const tiersBelowPackageMinimum = (opp: UpsizeOpp, packages: ExposurePackage[], country?: string): boolean => {
  const min = resolveExposurePackage(packages, opp.package, country)?.minCommissionRate || 0;
  if (min <= 0) return false;
  return opp.tiers.some((t) => t.unit === '%' && Number(t.rate) < min);
};

export const fmtMonthDay = (d: Date | null): string =>
  d ? d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short' }) : '—';

export const fmtFull = (d: Date | null): string =>
  d ? d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
